import { useEffect, useRef, useState } from "react";
import * as THREE from "three";

/**
 * Horizontal strip texture — one long image tiled from several thumbs and
 * repeated along the ribbon's U axis. Kept at moderate resolution to avoid
 * frame drops while dragging.
 */
const STRIP_TILES = [
  "https://readdy.ai/api/search-image?query=Modern%20editorial%20magazine%20cover%20mockup%20on%20warm%20cream%20background%20with%20bold%20black%20typography%20and%20a%20single%20abstract%20orange%20circle%2C%20clean%20minimalist%20graphic%20design%20layout%20photographed%20under%20soft%20natural%20window%20light&width=768&height=512&seq=ribbon-tile-01&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Cinematic%20portrait%20of%20a%20human%20figure%20lit%20with%20intense%20red%20studio%20light%20against%20deep%20black%20backdrop%2C%20dramatic%20chiaroscuro%2C%20high%20contrast%20fashion%20photography%20with%20subtle%20film%20grain%20and%20warm%20tonal%20shadows&width=768&height=512&seq=ribbon-tile-02&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Abstract%20fluid%20paint%20swirls%20in%20warm%20terracotta%20cream%20and%20soft%20amber%20tones%20on%20ivory%20canvas%2C%20macro%20photography%20with%20smooth%20organic%20gradients%20and%20delicate%20highlights%2C%20luxurious%20minimalist%20wall%20art%20aesthetic&width=768&height=512&seq=ribbon-tile-03&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Warm%20interior%20architectural%20detail%20with%20curved%20archway%20soft%20linen%20drapery%20and%20natural%20oak%20flooring%2C%20golden%20afternoon%20light%2C%20magazine%20editorial%20photography%20with%20refined%20negative%20space%20and%20subtle%20shadows&width=768&height=512&seq=ribbon-tile-04&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Dark%20studio%20portrait%20of%20a%20model%20with%20dramatic%20crimson%20light%20across%20the%20face%2C%20artistic%20avant%20garde%20fashion%20photography%2C%20cinematic%20mood%20lighting%20on%20black%20background%20with%20warm%20amber%20highlights&width=768&height=512&seq=ribbon-tile-05&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Minimalist%20typography%20poster%20design%20with%20large%20serif%20wordmark%20on%20aged%20paper%2C%20warm%20cream%20background%20with%20faint%20paper%20texture%2C%20refined%20modernist%20layout%20photographed%20with%20soft%20directional%20light&width=768&height=512&seq=ribbon-tile-06&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Sand%20dunes%20at%20sunset%20with%20soft%20ripples%20and%20warm%20amber%20light%20casting%20long%20shadows%2C%20abstract%20natural%20landscape%20photography%20with%20cinematic%20color%20grading%20and%20refined%20film%20grain&width=768&height=512&seq=ribbon-tile-07&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Elegant%20still%20life%20of%20dried%20pampas%20grass%20in%20ceramic%20vase%20on%20warm%20beige%20surface%2C%20soft%20directional%20window%20light%2C%20fine%20art%20botanical%20photography%20with%20subtle%20natural%20shadows&width=768&height=512&seq=ribbon-tile-08&orientation=landscape",
];

async function buildStripTexture(): Promise<THREE.Texture> {
  const tileW = 512;
  const tileH = 512;
  const loaded = await Promise.all(
    STRIP_TILES.map(
      (src) =>
        new Promise<HTMLImageElement | null>((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null);
          img.src = src;
        }),
    ),
  );

  const canvas = document.createElement("canvas");
  canvas.width = tileW * STRIP_TILES.length;
  canvas.height = tileH;
  const ctx = canvas.getContext("2d")!;

  loaded.forEach((img, i) => {
    const x = i * tileW;
    if (!img) {
      ctx.fillStyle = i % 2 === 0 ? "#efe2ce" : "#d9c2a1";
      ctx.fillRect(x, 0, tileW, tileH);
      return;
    }
    const iw = img.width;
    const ih = img.height;
    const scale = Math.max(tileW / iw, tileH / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    const dx = x + (tileW - dw) / 2;
    const dy = (tileH - dh) / 2;
    ctx.drawImage(img, dx, dy, dw, dh);
  });

  const grad = ctx.createLinearGradient(0, 0, 0, tileH);
  grad.addColorStop(0, "rgba(255, 240, 220, 0.05)");
  grad.addColorStop(1, "rgba(60, 40, 20, 0.12)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, canvas.width, tileH);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.anisotropy = 4;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Build a proper HELIX ribbon that wraps around a central axis.
 *
 * Key stability tricks (fixing the frame-flip / self-intersection at ends):
 *  1. Build control points on an EXTENDED parameter range [-GUARD, 1+GUARD]
 *     and only render the interior interval [0, 1]. This lets the tangent
 *     estimator "see past" the visible endpoints so the Frenet frames stay
 *     stable right up to the visible boundary.
 *  2. Use a PARALLEL-TRANSPORT base frame (not raw curve.getTangentAt-only
 *     Gram-Schmidt) — we carry the reference normal forward from the
 *     previous sample by projecting it into the plane perpendicular to the
 *     new tangent. This prevents the sudden 180° "flip" that plain
 *     up-vector Gram-Schmidt can produce when the tangent swings.
 *  3. The extra axial twist (theta) is EASED with a smootherstep so its
 *     rate is zero at both ends and peaks near the middle. That way we
 *     never pile a large rotation rate onto the unstable boundary region.
 *  4. The strip texture is faded to zero alpha near the visible U ends so
 *     any residual imperfection dissolves into the background instead of
 *     mixing with the fresnel dispersion.
 */
function buildHelixRibbon(visibleSamples: number) {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];

  // Extended parameter range: guard on both sides
  const GUARD = 0.12; // 12% overhang on each side
  const T_MIN = -GUARD;
  const T_MAX = 1 + GUARD;

  // Path definition — a genuine helix wrapping around the X axis.
  // The path is SYMMETRIC about t = 0.5 so both ends look identical,
  // and the Y/Z components together trace a circle around the central
  // X axis (that's what makes it a real helix, not a wavy line).
  const LENGTH_X = 11.6;
  const HELIX_TURNS = 1; // one full wrap around the central axis
  const HELIX_RADIUS_Y = 0.55;
  const HELIX_RADIUS_Z = 1.25;
  const pathFn = (t: number, out: THREE.Vector3) => {
    const nt = t * 2 - 1; // -1..1
    const x = nt * LENGTH_X * 0.5;
    // Helix angle — one full turn across the visible span, phase-shifted
    // so both endpoints land on the same (front-of-camera) side.
    const phi = t * HELIX_TURNS * Math.PI * 2 + Math.PI * 0.5;
    const y = Math.cos(phi) * HELIX_RADIUS_Y;
    const z = Math.sin(phi) * HELIX_RADIUS_Z;
    out.set(x, y, z);
    return out;
  };

  // Build control points for the CatmullRomCurve3 across the EXTENDED
  // range so the tangent estimator at t = 0 and t = 1 has real neighbors
  // on both sides (no open-curve edge extrapolation instability).
  const CONTROL = 80;
  const controlPoints: THREE.Vector3[] = [];
  for (let i = 0; i < CONTROL; i += 1) {
    const t = T_MIN + (T_MAX - T_MIN) * (i / (CONTROL - 1));
    const v = new THREE.Vector3();
    pathFn(t, v);
    controlPoints.push(v);
  }

  const curve = new THREE.CatmullRomCurve3(
    controlPoints,
    false,
    "catmullrom",
    0.5,
  );

  // Map visible t in [0,1] back to the curve's u in [0,1] where the
  // guarded interior sits between uStart and uEnd.
  const uStart = GUARD / (1 + 2 * GUARD);
  const uEnd = (1 + GUARD) / (1 + 2 * GUARD);
  const uOf = (visibleT: number) => uStart + (uEnd - uStart) * visibleT;

  // Width function (sine-based, WIDE in middle, tapering to sharp points
  // at both ends) — gives the classic leaf/lens silhouette from the ref.
  const widthAt = (t: number) => {
    const wMin = 1.35; // ends stay clearly visible / chunky
    const wMax = 3.6;  // middle is now noticeably wider
    // sin(pi*t) equals 0 at t=0/1 and 1 at t=0.5 -> smooth sine bulge.
    const bulge = Math.sin(Math.PI * t);
    return wMin + (wMax - wMin) * bulge;
  };

  // Surface roll around the tangent. Kept SYMMETRIC so both ends match:
  // theta(t) = totalTwist * s(t) where s(t) is a smoothstep that goes
  // 0 -> 1 with zero derivative at both boundaries. That way we still
  // get one full turn end-to-end, but the twist rate is gentle where
  // frames are most sensitive.
  const TWIST_TURNS = 1; // one full surface rotation end-to-end
  const totalTwist = TWIST_TURNS * Math.PI * 2;
  const smootherstep = (x: number) => {
    const c = Math.max(0, Math.min(1, x));
    return c * c * c * (c * (c * 6 - 15) + 10);
  };
  const twistAt = (t: number) => totalTwist * smootherstep(t);

  // Parallel-transport frame: carry the reference normal along the curve
  // by projecting it into the plane perpendicular to each new tangent.
  // Initialise with a stable up.
  const worldUp = new THREE.Vector3(0, 1, 0);
  const worldZ = new THREE.Vector3(0, 0, 1);

  const p = new THREE.Vector3();
  const tan = new THREE.Vector3();
  const prevTan = new THREE.Vector3();
  const refNormal = new THREE.Vector3();
  const finalNormal = new THREE.Vector3();
  const left = new THREE.Vector3();
  const right = new THREE.Vector3();
  const surfNormal = new THREE.Vector3();
  const rotAxis = new THREE.Vector3();

  // Seed the reference normal at the first sample.
  {
    const u0 = uOf(0);
    curve.getTangentAt(u0, prevTan).normalize();
    let up = worldUp;
    if (Math.abs(prevTan.dot(worldUp)) > 0.95) up = worldZ;
    refNormal
      .copy(up)
      .addScaledVector(prevTan, -up.dot(prevTan))
      .normalize();
  }

  for (let i = 0; i < visibleSamples; i += 1) {
    const t = i / (visibleSamples - 1);
    const u = uOf(t);
    curve.getPointAt(u, p);
    curve.getTangentAt(u, tan).normalize();

    if (i > 0) {
      // Rotate refNormal by the small rotation that took prevTan -> tan.
      // (Rodrigues via applyAxisAngle on the shortest-arc rotation.)
      const dot = Math.max(-1, Math.min(1, prevTan.dot(tan)));
      const angle = Math.acos(dot);
      if (angle > 1e-5) {
        rotAxis.crossVectors(prevTan, tan);
        if (rotAxis.lengthSq() > 1e-10) {
          rotAxis.normalize();
          refNormal.applyAxisAngle(rotAxis, angle);
        }
      }
      // Re-orthogonalise against the current tangent to kill drift.
      refNormal
        .addScaledVector(tan, -refNormal.dot(tan))
        .normalize();
    }
    prevTan.copy(tan);

    // Now apply the EXPLICIT axial twist on top of the stable frame.
    const theta = twistAt(t);
    finalNormal.copy(refNormal).applyAxisAngle(tan, theta);

    const w = widthAt(t);
    left.copy(p).addScaledVector(finalNormal, w * 0.5);
    right.copy(p).addScaledVector(finalNormal, -w * 0.5);

    positions.push(left.x, left.y, left.z);
    positions.push(right.x, right.y, right.z);

    // Surface normal for lighting/fresnel = tangent × width-direction
    surfNormal.crossVectors(tan, finalNormal).normalize();
    normals.push(surfNormal.x, surfNormal.y, surfNormal.z);
    normals.push(surfNormal.x, surfNormal.y, surfNormal.z);

    // UVs: U along the path, V across the ribbon
    uvs.push(t, 1);
    uvs.push(t, 0);
  }

  for (let i = 0; i < visibleSamples - 1; i += 1) {
    const a = i * 2;
    const b = i * 2 + 1;
    const c = (i + 1) * 2;
    const d = (i + 1) * 2 + 1;
    indices.push(a, b, c);
    indices.push(b, d, c);
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geom.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geom.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geom.setIndex(indices);
  geom.computeBoundingSphere();
  return geom;
}

export default function RibbonGallery() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let width = container.clientWidth;
    let height = container.clientHeight;

    const scene = new THREE.Scene();
    scene.background = null;

    const camera = new THREE.PerspectiveCamera(38, width / height, 0.1, 100);
    camera.position.set(0, 0, 8.2);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
    renderer.setSize(width, height);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);

    // ~210 samples across the visible interval — enough for a silky twist.
    const SAMPLES = 210;
    const geometry = buildHelixRibbon(SAMPLES);

    const placeholder = new THREE.DataTexture(
      new Uint8Array([230, 220, 200, 255]),
      1,
      1,
      THREE.RGBAFormat,
    );
    placeholder.needsUpdate = true;

    const uniforms = {
      uMap: { value: placeholder as THREE.Texture },
      uProgress: { value: 0.0 },
      uTime: { value: 0.0 },
      uFresnelStrength: { value: 1.25 },
      uDispersion: { value: 0.05 },
    };

    const material = new THREE.ShaderMaterial({
      uniforms,
      side: THREE.DoubleSide,
      transparent: true,
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vWorldNormal;
        varying vec3 vViewDir;

        void main() {
          vUv = uv;
          vec4 worldPos = modelMatrix * vec4(position, 1.0);
          vWorldNormal = normalize(mat3(modelMatrix) * normal);
          vViewDir = normalize(cameraPosition - worldPos.xyz);
          gl_Position = projectionMatrix * viewMatrix * worldPos;
        }
      `,
      fragmentShader: `
        precision highp float;
        uniform sampler2D uMap;
        uniform float uProgress;
        uniform float uTime;
        uniform float uFresnelStrength;
        uniform float uDispersion;

        varying vec2 vUv;
        varying vec3 vWorldNormal;
        varying vec3 vViewDir;

        void main() {
          // Slide the texture along U by the global progress (looping cleanly
          // because the texture is set to RepeatWrapping).
          vec2 uv = vec2(vUv.x + uProgress, vUv.y);

          // Fresnel: |N·V| is small at grazing angles → edges glow strongest.
          // Using abs() keeps the effect symmetric on the DoubleSide material.
          float NdV = clamp(abs(dot(normalize(vWorldNormal), normalize(vViewDir))), 0.0, 1.0);
          float fres = pow(1.0 - NdV, 2.3) * uFresnelStrength;

          // Damp fresnel where the surface is nearly edge-on to the camera
          // (NdV very small) to avoid amplifying artefacts on razor-thin
          // slivers that are typical near self-intersections.
          float surfaceMask = smoothstep(0.02, 0.18, NdV);
          fres *= surfaceMask;

          // Chromatic dispersion — sample R/G/B at slightly shifted UVs.
          // Amount is proportional to fresnel so it shows only near edges.
          float shift = uDispersion * fres;
          float r = texture2D(uMap, uv + vec2( shift,  0.0)).r;
          float g = texture2D(uMap, uv).g;
          float b = texture2D(uMap, uv + vec2(-shift,  0.0)).b;
          vec3 col = vec3(r, g, b);

          // Prismatic rainbow tint layered onto the edges (red / yellow / blue-ish).
          vec3 rainbow = mix(vec3(1.35, 0.35, 0.35), vec3(0.35, 0.5, 1.35), vUv.y);
          rainbow = mix(rainbow, vec3(1.4, 1.25, 0.35),
                       sin(vUv.x * 20.0 + uTime * 0.8) * 0.5 + 0.5);
          col += rainbow * fres * 0.55;

          // No end-fade — both tips of the ribbon stay fully visible.
          // Only fade opacity where the surface is nearly edge-on to the
          // camera to avoid amplifying razor-thin sliver artefacts.
          float alpha = mix(0.55, 1.0, surfaceMask);

          gl_FragColor = vec4(col, alpha);
        }
      `,
    });

    const ribbon = new THREE.Mesh(geometry, material);
    scene.add(ribbon);

    const ambient = new THREE.AmbientLight(0xffffff, 0.9);
    scene.add(ambient);

    buildStripTexture()
      .then((tex) => {
        material.uniforms.uMap.value = tex;
        material.needsUpdate = true;
        setReady(true);
      })
      .catch(() => setReady(true));

    // --------------- Interaction ---------------
    let progress = 0;
    let velocity = 0;
    let dragging = false;
    let lastX = 0;
    const samplesArr: number[] = [];
    const MAX_SAMPLES = 6;

    const canvasEl = renderer.domElement;
    canvasEl.style.cursor = "grab";
    canvasEl.style.touchAction = "none";

    const onDown = (ev: PointerEvent) => {
      dragging = true;
      setIsDragging(true);
      lastX = ev.clientX;
      velocity = 0;
      samplesArr.length = 0;
      canvasEl.setPointerCapture(ev.pointerId);
      canvasEl.style.cursor = "grabbing";
    };
    const onMove = (ev: PointerEvent) => {
      if (!dragging) return;
      const dx = ev.clientX - lastX;
      lastX = ev.clientX;
      const delta = -dx * 0.0014;
      progress += delta;
      samplesArr.push(delta);
      if (samplesArr.length > MAX_SAMPLES) samplesArr.shift();
    };
    const onUp = (ev: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      setIsDragging(false);
      try {
        canvasEl.releasePointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
      canvasEl.style.cursor = "grab";
      if (samplesArr.length > 0) {
        velocity =
          samplesArr.reduce((a, b) => a + b, 0) / samplesArr.length;
      }
    };

    canvasEl.addEventListener("pointerdown", onDown);
    canvasEl.addEventListener("pointermove", onMove);
    canvasEl.addEventListener("pointerup", onUp);
    canvasEl.addEventListener("pointercancel", onUp);
    canvasEl.addEventListener("pointerleave", onUp);

    const IDLE_SPEED = 0.0006;

    // --------------- Animation loop ---------------
    let raf = 0;
    const clock = new THREE.Clock();
    const animate = () => {
      const elapsed = clock.getElapsedTime();

      if (!dragging) {
        if (Math.abs(velocity) > 0.00002) {
          progress += velocity;
          velocity *= 0.95; // inertia decay
          if (Math.abs(velocity) < 0.00002) velocity = 0;
        } else {
          progress += IDLE_SPEED;
        }
      }

      // Modulo for seamless loop
      progress = progress - Math.floor(progress);

      material.uniforms.uProgress.value = progress;
      material.uniforms.uTime.value = elapsed;

      // Slow parallax rotation to help sell the 3D depth
      ribbon.rotation.y = Math.sin(elapsed * 0.15) * 0.06;
      ribbon.rotation.x = Math.sin(elapsed * 0.1) * 0.03;

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      if (!container) return;
      width = container.clientWidth;
      height = container.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    const ro = new ResizeObserver(handleResize);
    ro.observe(container);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      canvasEl.removeEventListener("pointerdown", onDown);
      canvasEl.removeEventListener("pointermove", onMove);
      canvasEl.removeEventListener("pointerup", onUp);
      canvasEl.removeEventListener("pointercancel", onUp);
      canvasEl.removeEventListener("pointerleave", onUp);
      geometry.dispose();
      material.dispose();
      placeholder.dispose();
      const mapVal = material.uniforms.uMap.value as THREE.Texture | null;
      if (mapVal && mapVal !== placeholder) mapVal.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div className="relative w-full h-full select-none">
      <div ref={containerRef} className="absolute inset-0" />
      {!ready && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="text-[11px] tracking-[0.4em] uppercase text-neutral-500">
            Loading helix…
          </div>
        </div>
      )}
      <div
        className={`pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-4 text-[10px] tracking-[0.4em] uppercase transition-opacity duration-300 ${
          isDragging ? "opacity-30" : "opacity-60"
        } text-neutral-500`}
      >
        Drag horizontally · release to fling
      </div>
    </div>
  );
}